import React, { useReducer, createContext, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { bookReducer, bookInitialState } from "../reducers";
import constant from "../constants/bookConstant";
import { bookService } from "../services";
import { toast } from "react-toastify";
import { history } from "../tools";

export const BookContext = createContext({
    state: bookInitialState,
    actions: {
        get: () => { },
        add: () => { },
        should: () => { },
        addReadList: () => { },
        searchAuthor: () => { },
        search: () => { },
        paginate: () => { },
        reset: () => { },
        goToDetail: () => { }
    }
});

export const BookContextProvider = ({ children }) => {
    const [state, dispatch] = useReducer(bookReducer, bookInitialState);

    const get = useCallback((id, isRequest = true) => {
        if (isRequest) dispatch({ type: constant.GET_REQUEST });

        bookService
            .get(id)
            .then((response) => {
                if (!response.success) {
                    toast.error(response.message);
                    dispatch({ type: constant.GET_FAIL });
                    return;
                }
                dispatch({ type: constant.GET_SUCCESS, payload: response.data });
            })
            .catch(() => {
                toast.error("the book couldn't be fetched!");
                dispatch({ type: constant.GET_FAIL });
            });
    }, []);

    const add = useCallback((book, onSuccess) => {
        dispatch({ type: constant.ADD_REQUEST });

        bookService
            .add(book)
            .then((response) => {
                if (!response.success) {
                    toast.error(response.message);
                    dispatch({ type: constant.ADD_FAIL });
                    return;
                }
                dispatch({ type: constant.ADD_SUCCESS, payload: response.data });
                if (onSuccess) onSuccess(response.data);
            })
            .catch(() => {
                toast.error("the book couldn't be added!");
                dispatch({ type: constant.ADD_FAIL });
            });
    }, []);

    const should = useCallback((dto, onSuccess) => {
        dispatch({ type: constant.SHOULD_REQUEST });

        bookService
            .should(dto)
            .then((response) => {
                if (!response.success) {
                    toast.error(response.message);
                    dispatch({ type: constant.SHOULD_FAIL });
                    return;
                }
                dispatch({ type: constant.SHOULD_SUCCESS, payload: response.data });
                if (onSuccess) onSuccess();
            })
            .catch(() => {
                toast.error("the book couldn't be rated!");
                dispatch({ type: constant.SHOULD_FAIL });
            });
    }, []);

    const addReadList = useCallback((dto, onSuccess) => {
        dispatch({ type: constant.ADD_LIST_REQUEST });

        bookService
            .addReadList(dto)
            .then((response) => {
                if (!response.success) {
                    toast.error(response.message);
                    dispatch({ type: constant.ADD_LIST_FAIL });
                    return;
                }
                dispatch({ type: constant.ADD_LIST_SUCCESS, payload: response.data });
                if (onSuccess) onSuccess();
            })
            .catch(() => {
                toast.error("The book couldn't be added to your reading list!");
                dispatch({ type: constant.ADD_LIST_FAIL });
            });
    }, []);

    const searchAuthor = useCallback((key) => {
        dispatch({ type: constant.SEARCH_AUTHOR_REQUEST });

        bookService
            .searchAuthor(key)
            .then((response) => {
                if (!response.success) {
                    toast.error(response.message);
                    dispatch({ type: constant.SEARCH_AUTHOR_FAIL });
                    return;
                }
                dispatch({ type: constant.SEARCH_AUTHOR_SUCCESS, payload: response.data });
            })
            .catch(() => {
                toast.error("authors couldn't be fetched!");
                dispatch({ type: constant.SEARCH_AUTHOR_FAIL });
            });
    }, []);

    const search = useCallback((key, isRequest = true) => {
        if (isRequest) dispatch({ type: constant.SEARCH_REQUEST });

        bookService
            .search(key)
            .then((response) => {
                if (!response.success) {
                    toast.error(response.message);
                    dispatch({ type: constant.SEARCH_FAIL });
                    return;
                }
                dispatch({ type: constant.SEARCH_SUCCESS, payload: response.data });
            })
            .catch(() => {
                toast.error("books couldn't be fetched!");
                dispatch({ type: constant.SEARCH_FAIL });
            });
    }, []);

    const paginate = useCallback((dto, onSuccess) => {
        const payload = { limit: 12, ...dto };
        dispatch({ type: constant.PAGINATE_REQUEST });

        bookService
            .paginate(payload)
            .then((response) => {
                if (!response.success) {
                    toast.error(response.message);
                    dispatch({ type: constant.PAGINATE_FAIL });
                    return;
                }
                dispatch({ type: constant.PAGINATE_SUCCESS, payload: response.data });
                if (onSuccess) onSuccess();
            })
            .catch(() => {
                toast.error("books couldn't be fetched");
                dispatch({ type: constant.PAGINATE_FAIL });
            });
    }, []);

    const goToDetail = useCallback((id) => {
        history.push(`/book/${id}`);
    }, []);

    const reset = useCallback(() => {
        dispatch({ type: constant.RESET });
    }, []);

    const actions = useMemo(
        () => ({
            add,
            search,
            paginate,
            searchAuthor,
            get,
            should,
            reset,
            addReadList,
            goToDetail
        }),
        [add, search, paginate, searchAuthor, get, should, reset, addReadList, goToDetail]
    );

    const value = useMemo(() => ({ state, actions }), [state, actions]);

    return <BookContext.Provider value={value}>{children}</BookContext.Provider>;
};

BookContextProvider.propTypes = {
    children: PropTypes.node.isRequired
};
